
import React, { useState, useEffect } from 'react';
import './Calendar.css';
import EventModal from './EventModal';
import HamburgerMenu from './HamburgerMenu';
import ManageUsersModal from './ManageUsersModal';

// Returns the heat-map state for a single calendar day.
// Exported for testing.
export function getDayState(date, events, blockedWeekdays, practiceWeekdays = [3]) {
  const weekday = date.getDay(); // 0=Sun,1=Mon,...,6=Sat
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  const dayEnd   = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);

  const overlapping = events.filter(e => {
    const s = e.start instanceof Date ? e.start : new Date(e.start);
    const en = e.end   instanceof Date ? e.end   : new Date(e.end);
    return s <= dayEnd && en >= dayStart;
  });

  if (overlapping.some(e => e.type === 'vacation')) return 'vacation';

  const hasBandEvent = overlapping.some(e => e.type === 'band_event' || e.type === 'booked' || e.type === 'practice' || !e.type);

  if (practiceWeekdays.includes(weekday) && !hasBandEvent) return 'free-wed';
  if ((weekday === 0 || weekday === 5 || weekday === 6) && !blockedWeekdays.includes(weekday) && !practiceWeekdays.includes(weekday) && !hasBandEvent) return 'free-weekend';

  return 'normal';
}

// Generates all day slots for a month view (padded to complete weeks).
function buildDays(currentDate) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({ date: new Date(year, month, -i), currentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ date: new Date(year, month, d), currentMonth: true });
  }
  while (days.length % 7 !== 0) {
    const last = days[days.length - 1].date;
    days.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), currentMonth: false });
  }
  return days;
}

const TYPE_LABEL = { band_event: 'GIG', booked: 'GIG', practice: 'PRACTICE', vacation: 'HOLIDAY' };

export function MonthGrid({ events, currentDate, blockedWeekdays, practiceWeekdays = [3], onDayClick = () => {} }) {
  const days = buildDays(currentDate);
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);

  return (
    <div>
      <div className="month-grid" style={{ marginBottom: 4 }}>
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(h => (
          <div key={h} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#6b7280', paddingBottom: 4 }}>{h}</div>
        ))}
      </div>
      <div className="month-grid">
        {days.map(({ date, currentMonth }) => {
          const state = getDayState(date, events, blockedWeekdays, practiceWeekdays);
          const isPast = date < todayMidnight;
          const dayEvents = events.filter(e => {
            const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
            const dayEnd   = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
            const s = e.start instanceof Date ? e.start : new Date(e.start);
            const en = e.end instanceof Date ? e.end : new Date(e.end);
            return s <= dayEnd && en >= dayStart;
          });
          const bandEvents     = dayEvents.filter(e => e.type === 'band_event' || e.type === 'booked' || !e.type);
          const practiceEvents = dayEvents.filter(e => e.type === 'practice');

          return (
            <div
              key={date.toISOString()}
              className={`cal-cell cal-cell--${state}${!currentMonth ? ' cal-cell--other-month' : ''}`}
              onClick={() => onDayClick(date, dayEvents)}
            >
              <span className="cal-cell__day-num">{date.getDate()}</span>
              {state === 'free-wed' && !isPast && <span className="cal-cell__label">PRACTICE?</span>}
              {state === 'free-weekend' && !isPast && <span className="cal-cell__label">Available</span>}
              {bandEvents.slice(0, 2).map((e) => (
                <span key={e.id ?? e.title} className="cal-cell__pill">{e.title || TYPE_LABEL[e.type] || 'GIG'}</span>
              ))}
              {practiceEvents.slice(0, 2).map((e) => (
                <span key={e.id ?? e.title} className="cal-cell__pill cal-cell__pill--practice">{e.title || 'PRACTICE'}</span>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CustomCalendar = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isManageUsersModalOpen, setIsManageUsersModalOpen] = useState(false);
  const [members, setMembers] = useState([]);

  const handleLogout = () => {
    window.location.href = '/logout';
  };

  const handleOpenManageUsersModal = () => {
    setIsManageUsersModalOpen(true);
    setIsMenuOpen(false); // Close hamburger menu when opening manage users modal
  };

  const handleCloseManageUsersModal = () => {
    setIsManageUsersModalOpen(false);
  };


  useEffect(() => {
    fetch('/api/events')
      .then((res) => res.json())
      .then((data) => setEvents(data.map(e => ({...e, start: new Date(e.start), end: new Date(e.end)}))));
  }, []);

  useEffect(() => {
    fetch('/api/admin/users')
      .then(r => r.ok ? r.json() : [])
      .then(data => setMembers(data))
      .catch(() => {});
  }, []);

  const [blockedWeekdays, setBlockedWeekdays] = useState([1, 2, 4]);
  const [practiceWeekdays, setPracticeWeekdays] = useState([3]);

  useEffect(() => {
    fetch('/api/admin/config/bookability')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setBlockedWeekdays(data.blockedWeekdays || [1, 2, 4]);
          setPracticeWeekdays(data.practiceWeekdays || [3]);
        }
      })
      .catch(() => {});
  }, []);

  const [selectedDay, setSelectedDay] = useState(null);   // { date, events }

  const handleDayClick = (date, dayEvents) => {
    setSelectedDay({ date, events: dayEvents });
  };

  const handleCloseDayDetail = () => setSelectedDay(null);

  const handleOpenModal = (event = null) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const handleSaveEvent = (eventData) => {
    const url = eventData.id ? `/api/events/${eventData.id}` : '/api/events';
    const method = eventData.id ? 'PUT' : 'POST';

    fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData),
    })
    .then(res => res.json())
    .then(savedEvent => {
      const newEvent = {...savedEvent, start: new Date(savedEvent.start), end: new Date(savedEvent.end)};
      if (method === 'POST') {
        setEvents([...events, newEvent]);
      } else {
        setEvents(events.map(e => e.id === newEvent.id ? newEvent : e));
      }
      handleCloseModal();
    });
  };

  const handleDeleteEvent = (eventId) => {
    fetch(`/api/events/${eventId}`, { method: 'DELETE' })
      .then(() => {
        setEvents(events.filter(e => e.id !== eventId));
        handleCloseModal();
      });
  };

  const renderHeader = () => (
    <div className="calendar-header">
      <button className="icon-button hamburger-icon" onClick={() => setIsMenuOpen(o => !o)}>&#9776;</button>
      <span className="calendar-title">Crazy Cow Calendar</span>
      <button className="icon-button add-icon" onClick={() => handleOpenModal()}>+</button>
      {isMenuOpen && <HamburgerMenu user={user} onLogout={handleLogout} onManageUsersClick={handleOpenManageUsersModal} onClose={() => setIsMenuOpen(false)} />}
    </div>
  );


  const changeMonth = (amount) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + amount, 1));
  };

  return (
    <div className="bg-gray-800 text-white rounded-lg p-4 shadow-lg w-full">
      {renderHeader()}
      <div className="flex justify-between items-center mb-4">
        <button onClick={() => changeMonth(-1)} className="text-white text-2xl">&lt;</button>
        <h2 className="text-xl font-bold">
          {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </h2>
        <button onClick={() => changeMonth(1)} className="text-white text-2xl">&gt;</button>
      </div>

      <MonthGrid
        events={events}
        currentDate={currentDate}
        blockedWeekdays={blockedWeekdays}
        practiceWeekdays={practiceWeekdays}
        onDayClick={handleDayClick}
      />

      {/* Day detail panel */}
      {selectedDay && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end justify-center z-50"
             onClick={handleCloseDayDetail}>
          <div className="bg-gray-800 rounded-t-2xl w-full max-w-lg p-6 pb-10"
               onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">
                {selectedDay.date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              </h3>
              <button onClick={handleCloseDayDetail} className="text-gray-400 text-2xl leading-none">&times;</button>
            </div>

            {(() => {
              const vacEvents    = selectedDay.events.filter(e => e.type === 'vacation');
              const bandEvts     = selectedDay.events.filter(e => e.type === 'band_event' || e.type === 'booked' || !e.type);
              const practiceEvts = selectedDay.events.filter(e => e.type === 'practice');
              return (
                <>
                  {vacEvents.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-bold text-orange-300 uppercase mb-2">Holiday</p>
                      {vacEvents.map((e) => (
                        <div key={e.id ?? e.title} className="text-sm text-gray-300 py-1 border-b border-gray-700 flex justify-between">
                          <span>{e.memberEmail} — {e.title}</span>
                          <button onClick={() => { handleCloseDayDetail(); handleOpenModal(e); }}
                                  className="text-orange-300 text-xs ml-4">Edit</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {bandEvts.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-bold text-blue-400 uppercase mb-2">Gigs</p>
                      {bandEvts.map((e) => (
                        <div key={e.id ?? e.title} className="text-sm text-gray-300 py-1 border-b border-gray-700 flex justify-between">
                          <span>{e.title}</span>
                          <button onClick={() => { handleCloseDayDetail(); handleOpenModal(e); }}
                                  className="text-blue-400 text-xs ml-4">Edit</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {practiceEvts.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-bold text-green-400 uppercase mb-2">Practice</p>
                      {practiceEvts.map((e) => (
                        <div key={e.id ?? e.title} className="text-sm text-gray-300 py-1 border-b border-gray-700 flex justify-between">
                          <span>{e.title}</span>
                          <button onClick={() => { handleCloseDayDetail(); handleOpenModal(e); }}
                                  className="text-green-400 text-xs ml-4">Edit</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {vacEvents.length === 0 && bandEvts.length === 0 && practiceEvts.length === 0 && (
                    <p className="text-sm text-gray-500 mb-4">Nothing scheduled.</p>
                  )}
                </>
              );
            })()}

            <button
              onClick={() => { handleCloseDayDetail(); handleOpenModal({ start: selectedDay.date, end: selectedDay.date, title: '' }); }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg mt-2"
            >
              + Add Event
            </button>
          </div>
        </div>
      )}

      {isModalOpen && (
        <EventModal
          event={selectedEvent || { start: new Date(), end: new Date(), title: '' }}
          onClose={handleCloseModal}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          currentUser={user}
          members={members}
        />
      )}

      {isManageUsersModalOpen && (
        <ManageUsersModal
          onClose={handleCloseManageUsersModal}
          currentUser={user}
        />
      )}
    </div>
  );
};

export default CustomCalendar;
